import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  Fab,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  MoreVert,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  Visibility,
  Download,
  Upload,
  FilterList,
  Sort,
  WhatsApp  // Added: 2026-01-19 - WhatsApp conversation initiation
} from '@mui/icons-material';
import { Email as EmailIcon } from '@mui/icons-material';
import EmailOutreachDialog from '../components/Email/EmailOutreachDialog'; // 09/02/2026 - Added for email outreach dialog
import { contactsAPI } from '../services/api';
import { ConversationInitiator } from '../components/conversations';  // Added: 2026-01-19
import { formatDate, formatDateTime } from '../utils/dateUtils'; // 08/02/2026 - Added for timezone implementation
interface Contact {
  id: number;
  name?: string;
  email?: string;
  phone_number?: string;
  avatar_url?: string;
  country?: string;
  city?: string;
  custom_attributes: Record<string, any>;
  created_at: string;
  conversations_count: number;
}

interface ContactFormData {
  name: string;
  email: string;
  phone_number: string;
  country: string;
  city: string;
  custom_attributes: Record<string, any>;
}


// Move ContactDialog outside component to prevent re-creation // added on 27/01/2026
const ContactDialog = React.memo(({ 
  open, 
  onClose, 
  title, 
  onSubmit, 
  submitLabel, 
  children 
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  onSubmit: () => void;
  submitLabel: string;
  children: React.ReactNode;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      {children}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onSubmit} variant="contained">
        {submitLabel}
      </Button>
    </DialogActions>
  </Dialog>
));

const ContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailOutreachOpen, setEmailOutreachOpen] = useState(false);
  const [emailOutreachContact, setEmailOutreachContact] = useState<Contact | null>(null); // 09/02/2026 - Added for email outreach dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [whatsappInitiatorOpen, setWhatsappInitiatorOpen] = useState(false);  // Added: 2026-01-19
  
  // Form state
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone_number: '',
    country: '',
    city: '',
    custom_attributes: {}
  });
  
  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionContactId, setActionContactId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [stats, setStats] = useState({
    total_contacts: 0,
    contacts_with_email: 0,
    contacts_with_phone: 0,
    contacts_with_conversations: 0,
    engagement_rate: 0
  });

  // Load stats only once on mount
  useEffect(() => {
    loadStats();
  }, []);

  // Load contacts with debouncing for search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadContacts();
    }, searchText ? 500 : 0); // 500ms debounce for search, immediate for other changes

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchText, sortBy, sortOrder]);

  const loadStats = async () => {
    try {
      const response = await contactsAPI.getContactStats();
      setStats(response);
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Set default stats if loading fails
      setStats({
        total_contacts: 0,
        contacts_with_email: 0,
        contacts_with_phone: 0,
        contacts_with_conversations: 0,
        engagement_rate: 0
      });
    }
  };

  /*
  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await contactsAPI.getContacts({
        // page: page + 1,
        // per_page: rowsPerPage,
        page: rowsPerPage === -1 ? 1 : page + 1, // If showing all, always page 1
        per_page: rowsPerPage === -1 ? 10000 : rowsPerPage, // Large number for "all"
        search: searchText || undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      // Debug: Log response to verify structure
      console.log('Contacts API Response:', response);

      // Handle response structure - check both possible formats
      const contactsList = response.contacts || response.data?.contacts || [];
      const totalCount = response.total || response.data?.total || response.count || 0;
      console.log('Contacts List:', contactsList);
      console.log('Total Count:', totalCount);
      // setContacts(response.contacts || []);
      // setTotal(response.total || 0);
      setContacts(contactsList);
      setTotal(totalCount);

      // Debug: Log pagination info
      console.log(`Loaded ${contactsList.length} contacts out of ${totalCount} total`);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      setSnackbar({ open: true, message: 'Failed to load contacts', severity: 'error' });
      // Set empty state on error
      setContacts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  */
  // Load contacts with infinite scroll using TanStack Query
  const loadContacts = async () => {
    try {
      setLoading(true);
      
      // If "All" is selected, fetch all contacts in batches
      if (rowsPerPage === -1) {
        let allContacts: Contact[] = [];
        let currentPage = 1;
        let hasMore = true;
        const batchSize = 100; // Backend max
        
        // Fetch all contacts in batches
        while (hasMore) {
          const response = await contactsAPI.getContacts({
            page: currentPage,
            per_page: batchSize,
            search: searchText || undefined,
            sort_by: sortBy,
            sort_order: sortOrder
          });
          
          const contactsList = response.contacts || response.data?.contacts || [];
          allContacts = [...allContacts, ...contactsList];
          
          // Check if there are more pages
          const totalCount = response.total || response.data?.total || 0;
          hasMore = allContacts.length < totalCount;
          currentPage++;
          
          // Safety limit to prevent infinite loops
          if (currentPage > 1000) {
            console.warn('Reached safety limit for fetching all contacts');
            break;
          }
        }
        
        setContacts(allContacts);
        setTotal(allContacts.length);
        console.log(`Loaded all ${allContacts.length} contacts`);
      } else {
        // Normal pagination
        const response = await contactsAPI.getContacts({
          page: page + 1,
          per_page: rowsPerPage,
          search: searchText || undefined,
          sort_by: sortBy,
          sort_order: sortOrder
        });
        
        // Debug: Log response to verify structure
        console.log('Contacts API Response:', response);
        
        // Handle response structure - check both possible formats
        const contactsList = response.contacts || response.data?.contacts || [];
        const totalCount = response.total || response.data?.total || response.count || 0;
        
        setContacts(contactsList);
        setTotal(totalCount);
        
        // Debug: Log pagination info
        console.log(`Loaded ${contactsList.length} contacts out of ${totalCount} total`);
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
      setSnackbar({ open: true, message: 'Failed to load contacts', severity: 'error' });
      // Set empty state on error
      setContacts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateContact = async () => {
    try {
      console.log('Creating contact with data:', formData);
      await contactsAPI.createContact(formData);
      setCreateDialogOpen(false);
      resetForm();
      loadContacts();
      loadStats(); // Refresh stats after creating
      setSnackbar({ open: true, message: 'Contact created successfully', severity: 'success' });
    } catch (error: any) {
      console.error('Failed to create contact:', error);
      console.error('Error response:', error.response?.data);
      setSnackbar({ open: true, message: error.response?.data?.detail || 'Failed to create contact', severity: 'error' });
    }
  };

  const handleUpdateContact = async () => {
    if (!selectedContact) return;
    
    try {
      await contactsAPI.updateContact(selectedContact.id, formData);
      setEditDialogOpen(false);
      resetForm();
      loadContacts();
      setSnackbar({ open: true, message: 'Contact updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to update contact:', error);
      setSnackbar({ open: true, message: 'Failed to update contact', severity: 'error' });
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    
    try {
      await contactsAPI.deleteContact(selectedContact.id);
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      loadContacts();
      loadStats(); // Refresh stats after deleting
      setSnackbar({ open: true, message: 'Contact deleted successfully', severity: 'success' });
    } catch (error: any) {
      console.error('Failed to delete contact:', error);
      const message = error.response?.data?.detail || 'Failed to delete contact';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone_number: '',
      country: '',
      city: '',
      custom_attributes: {}
    });
  }, []);

  /*
  // Optimized form field handlers to prevent re-rendering
  const handleFormFieldChange = useCallback((field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);
  */
    // Optimized form field handlers - create stable handlers for each field
  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: event.target.value }));
  }, []);

  const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, email: event.target.value }));
  }, []);

  const handlePhoneChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, phone_number: event.target.value }));
  }, []);

  const handleCountryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, country: event.target.value }));
  }, []);

  const handleCityChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, city: event.target.value }));
  }, []);
  
  const handleExportContacts = async () => {
    try {
      await contactsAPI.exportContacts();
      setSnackbar({ open: true, message: 'Contacts exported as CSV', severity: 'success' });
    } catch (error) {
      console.error('Failed to export contacts:', error);
      setSnackbar({ open: true, message: 'Failed to export contacts', severity: 'error' });
    }
  };

  const handleImportContacts = async (file: File) => {
    try {
      const response = await contactsAPI.importContacts(file);
      setSnackbar({ 
        open: true, 
        message: `Import completed: ${response.imported_count} contacts imported`, 
        severity: 'success' 
      });
      loadContacts();
      loadStats();
    } catch (error) {
      console.error('Failed to import contacts:', error);
      setSnackbar({ open: true, message: 'Failed to import contacts', severity: 'error' });
    }
  };

  const handleFileImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        handleImportContacts(file);
      }
    };
    input.click();
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone_number: contact.phone_number || '',
      country: contact.country || '',
      city: contact.city || '',
      custom_attributes: contact.custom_attributes || {}
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const openViewDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setViewDialogOpen(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, contactId: number) => {
    setAnchorEl(event.currentTarget);
    setActionContactId(contactId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionContactId(null);
  };

  // Memoize dialog callbacks to prevent re-renders
  const handleCloseCreateDialog = useCallback(() => {
  setCreateDialogOpen(false);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
  setEditDialogOpen(false);
  }, []);	

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
  };

  // commented out on 08/02/2026 by NK for timezone implementation
  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString([], {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric'
  //   });
  // };

  /*
  const ContactDialog = ({ 
    open, 
    onClose, 
    title, 
    onSubmit, 
    submitLabel, 
    children 
  }: {
    open: boolean;
    onClose: () => void;
    title: string;
    onSubmit: () => void;
    submitLabel: string;
    children: React.ReactNode;
  }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
  */
  /* commented out on 27/01/2026 by NK
  // Contact form fields - use memoized callbacks to prevent re-rendering
  const renderContactForm = useCallback(() => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={handleFormFieldChange('name')}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleFormFieldChange('email')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phone_number}
            onChange={handleFormFieldChange('phone_number')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Country"
            value={formData.country}
            onChange={handleFormFieldChange('country')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={handleFormFieldChange('city')}
          />
        </Grid>
      </Grid>
    </Box>
  ), [formData, handleFormFieldChange]);
  */

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Contacts
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<WhatsApp />}
              onClick={() => {
                setSelectedContact(null); // Clear any preselected contact
                setWhatsappInitiatorOpen(true);
              }}
              sx={{ 
                color: '#25D366',
                borderColor: '#25D366',
                '&:hover': {
                  borderColor: '#25D366',
                  backgroundColor: 'rgba(37, 211, 102, 0.08)'
                }
              }}
            >
              Start WhatsApp Chat
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => {
                setEmailOutreachContact(null);
                setEmailOutreachOpen(true);
              }}
              sx={{
                color: '#1976d2',
                borderColor: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              Send Email Outreach
            </Button>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={handleFileImport}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportContacts}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateDialog}
            >
              Add Contact
            </Button>
          </Box>
          {/* Email Outreach Dialog */}
          {/* Email Outreach Dialog */}
          <EmailOutreachDialog
            open={emailOutreachOpen}
            onClose={() => {
              setEmailOutreachOpen(false);
              setEmailOutreachContact(null);
            }}
            preselectedContact={emailOutreachContact}
            onSuccess={() => {
              setSnackbar({ open: true, message: 'Email sent successfully', severity: 'success' });
              loadContacts();
            }}
          />
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {stats.total_contacts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Contacts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {stats.contacts_with_email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  With Email
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">
                  {stats.contacts_with_phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  With Phone
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {stats.engagement_rate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Engagement Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search contacts..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="created_at">Created Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              label="Order"
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Total: ${total}`} color="primary" />
          <Chip label={`Showing: ${contacts.length} of ${total}`} variant="outlined" />
          {total > rowsPerPage && (
            <Chip 
              label={`Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`} 
              variant="outlined" 
              color="info"
            />
          )}
        </Box>
      </Paper>

      {/* Contacts Table */}
      <Paper sx={{ flex: 1,display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Conversations</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4 }}>
                      <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No contacts found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start by adding your first contact
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={contact.avatar_url}>
                          {getInitials(contact.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {contact.name || 'Unnamed Contact'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            #{contact.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {contact.email && <Email fontSize="small" color="action" />}
                        {contact.email || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {contact.phone_number && <Phone fontSize="small" color="action" />}
                        {contact.phone_number || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(contact.city || contact.country) && <LocationOn fontSize="small" color="action" />}
                        {[contact.city, contact.country].filter(Boolean).join(', ') || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={contact.conversations_count} 
                        size="small" 
                        color={contact.conversations_count > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(contact.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuClick(e, contact.id)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination - Always visible at bottom */}
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => {
            setPage(newPage);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newRowsPerPage = parseInt(e.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0); // Reset to first page when changing rows per page
          }}
          // rowsPerPageOptions={[10, 25, 50, 100]}
          rowsPerPageOptions={[10, 25, 50, 100, { value: -1, label: 'All' }]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
        </Box>  
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const contact = contacts.find(c => c.id === actionContactId);
          if (contact) openViewDialog(contact);
          handleMenuClose();
        }}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => {
          const contact = contacts.find(c => c.id === actionContactId);
          if (contact) openEditDialog(contact);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          const contact = contacts.find(c => c.id === actionContactId);
          if (contact) openDeleteDialog(contact);
          handleMenuClose();
        }}>
          <Delete sx={{ mr: 1 }} /> Delete
        </MenuItem>
        {/* Added: 2026-01-19 - WhatsApp conversation initiation */}
        <MenuItem onClick={() => {
          const contact = contacts.find(c => c.id === actionContactId);
          if (contact) {
            setSelectedContact(contact);
            setWhatsappInitiatorOpen(true);
          }
          handleMenuClose();
        }}>
          <WhatsApp sx={{ mr: 1, color: '#25D366' }} /> Start WhatsApp Chat
        </MenuItem>
      </Menu>
		
      {/* Create Contact Dialog */}
		  <ContactDialog
			open={createDialogOpen}
			//onClose={() => setCreateDialogOpen(false)}
			onClose={handleCloseCreateDialog}
			title="Create New Contact"
			onSubmit={handleCreateContact}
			submitLabel="Create Contact"
		  >
        {/* {renderContactForm()} */}
        {/* commented out on 27/01/2026 by NK */}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                //onChange={handleFormFieldChange('name')}
                onChange={handleNameChange}
				required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                //onChange={handleFormFieldChange('email')}
				onChange={handleEmailChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                //onChange={handleFormFieldChange('phone_number')}
				onChange={handlePhoneChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                //onChange={handleFormFieldChange('country')}
				onChange={handleCountryChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                //onChange={handleFormFieldChange('city')}
				onChange={handleCityChange}
              />
            </Grid>
          </Grid>
        </Box>
      </ContactDialog>

      {/* Edit Contact Dialog */}
      <ContactDialog
        open={editDialogOpen}
        //onClose={() => setEditDialogOpen(false)}
        onClose={handleCloseEditDialog}
		title="Edit Contact"
        onSubmit={handleUpdateContact}
        submitLabel="Update Contact"
      >
        {/* {renderContactForm()} */}
        {/* commented out on 27/01/2026 by NK */}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                //onChange={handleFormFieldChange('name')}
                onChange={handleNameChange}
				required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                //onChange={handleFormFieldChange('email')}
				onChange={handleEmailChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                //onChange={handleFormFieldChange('phone_number')}
				onChange={handlePhoneChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                //onChange={handleFormFieldChange('country')}
				onChange={handleCountryChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                //onChange={handleFormFieldChange('city')}
				onChange={handleCityChange}
              />
            </Grid>
          </Grid>
        </Box>
      </ContactDialog>

      {/* View Contact Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Contact Details</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar 
                      src={selectedContact.avatar_url}
                      sx={{ width: 80, height: 80 }}
                    >
                      {getInitials(selectedContact.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedContact.name || 'Unnamed Contact'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Contact #{selectedContact.id}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Email color="action" />
                    <Typography>{selectedContact.email || 'No email'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Phone color="action" />
                    <Typography>{selectedContact.phone_number || 'No phone'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn color="action" />
                    <Typography>
                      {[selectedContact.city, selectedContact.country].filter(Boolean).join(', ') || 'No location'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Statistics</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip label={`${selectedContact.conversations_count} Conversations`} color="primary" />
                    <Chip label={`Created ${formatDate(selectedContact.created_at)}`} variant="outlined" />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete contact "{selectedContact?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteContact} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* WhatsApp Conversation Initiator - Added: 2026-01-19 */}
      <ConversationInitiator
        open={whatsappInitiatorOpen}
        onClose={() => {
          setWhatsappInitiatorOpen(false);
          setSelectedContact(null);
        }}
        onConversationCreated={(conversationId) => {
          setWhatsappInitiatorOpen(false);
          setSelectedContact(null);
          setSnackbar({
            open: true,
            message: `WhatsApp conversation started successfully! Conversation ID: ${conversationId}`,
            severity: 'success'
          });
        }}
        preselectedContact={selectedContact ? {
          id: selectedContact.id,
          name: selectedContact.name || '',
          phone_number: selectedContact.phone_number || ''
        } : undefined}
      />
    </Box>
  );
};

export default ContactsPage;